import parse, { type DOMNode, Element, Text } from "html-react-parser"

import { ResponsiveAsset } from "~/components/primitives/ResponsiveAsset/ResponsiveAsset"

import type { Pill } from "./TextWithPill.interface"
import s from "./TextWithPill.module.scss"

const PillRenderer = ({ pill }: { pill: Pill }) => {
  if (!pill) return null

  switch (pill.type) {
    case "text":
      return <span className={s.pillText}>{pill.text}</span>

    case "image":
      return (
        <span className={s.pillImage} aria-hidden="true">
          <ResponsiveAsset
            desktop={pill.asset}
            imageSizes={{ base: "120px" }}
            loading="eager"
            animateOnLoad={false}
          />
        </span>
      )

    case "video":
      return (
        <span className={s.pillVideo} aria-hidden="true">
          <ResponsiveAsset
            desktop={pill.asset}
            imageSizes={{ base: "120px" }}
            loading="eager"
            animateOnLoad={false}
          />
        </span>
      )

    default:
      return null
  }
}

/**
 * Parses description HTML and replaces {pill} placeholder with the pill content.
 * If no placeholder exists but pill content is provided, appends it at the end.
 */
export const TextWithPill = ({
  content,
  pill,
}: {
  content: string
  pill: Pill
}) => {
  const hasPillPlaceholder = content.includes("{pill}")

  if (!hasPillPlaceholder) {
    // No placeholder - render description and append pill at end if exists
    return (
      <>
        {parse(content)}
        {pill && <PillRenderer pill={pill} />}
      </>
    )
  }

  // Replace {pill} placeholder with actual pill content
  const replaceOptions = {
    replace: (domNode: DOMNode) => {
      if (domNode instanceof Element) {
        return undefined
      }
      if (domNode instanceof Text && "data" in domNode) {
        const text = domNode.data as string
        if (text.includes("{pill}")) {
          const parts = text.split("{pill}")
          return (
            <>
              {parts.map((part, index) => (
                <span key={index}>
                  {part}
                  {index < parts.length - 1 && pill && (
                    <PillRenderer pill={pill} />
                  )}
                </span>
              ))}
            </>
          )
        }
      }
      return undefined
    },
  }

  return <>{parse(content, replaceOptions)}</>
}
